/**
 * tRPC client wired into TanStack Query.
 *
 * Usage in a route/component:
 *
 *   const trpc = useTRPC()
 *   const { data } = useQuery(trpc.tickets.list.queryOptions())
 *   const create = useMutation(trpc.tickets.create.mutationOptions())
 */
import { QueryClient } from '@tanstack/react-query'
import { createTRPCClient, httpBatchLink } from '@trpc/client'
import { createTRPCContext, createTRPCOptionsProxy } from '@trpc/tanstack-react-query'
import superjson from 'superjson'

import type { AppRouter } from '@server/routers/_app'

export const { TRPCProvider, useTRPC, useTRPCClient } = createTRPCContext<AppRouter>()

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Do not retry on authentication/permission errors.
        const code = (error as { data?: { code?: string } })?.data?.code

        if (code === 'UNAUTHORIZED' || code === 'FORBIDDEN' || code === 'NOT_FOUND') return false

        return failureCount < 2
      }
    }
  }
})

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: '/trpc',
      transformer: superjson,
      // Sends the session cookie along.
      fetch: (url, options) => fetch(url, { ...options, credentials: 'include' })
    })
  ]
})

/**
 * The same proxy, usable outside React components — TanStack Router loaders
 * and `beforeLoad`. It shares query keys with `useTRPC()`, so whatever the
 * route prefetches the component reuses without a refetch.
 */
export const trpc = createTRPCOptionsProxy<AppRouter>({ client: trpcClient, queryClient })
